// src/components/themes/default/store/branch/BranchCustomerDashboard.tsx
"use client";

import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import {
  ShoppingBag,
  Store,
  Clock,
  CheckCircle2,
  Package,
  XCircle,
  Truck,
  ArrowLeft,
  Search,
  User,
  Phone,
  Mail,
  MapPin,
  LogOut,
  RefreshCw,
  Plus,
  Minus,
  Trash2,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";
import { useBranchCart } from "./BranchCartContext";
import BranchCheckoutView from "./BranchCheckoutView";

interface BranchCustomerDashboardProps {
  branch: {
    id: number;
    name: string;
    slug: string;
    contact_phone?: string;
    address?: string;
  };
  customerUser: any;
  subdomain: string;
  primaryColor?: string;
  onBackToStore: () => void;
  onLogout: () => void;
}

export default function BranchCustomerDashboard({
  branch,
  customerUser,
  subdomain,
  primaryColor = "#059669",
  onBackToStore,
  onLogout,
}: BranchCustomerDashboardProps) {
  const [activeTab, setActiveTab] = useState<"orders" | "cart" | "profile" | "checkout">("orders");
  const [orders, setOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);

  const { cart, updateQuantity, removeFromCart, clearCart, cartTotal, totalItemCount } = useBranchCart();

  const getApiUrl = (endpoint: string) => {
    const isLocal =
      typeof window !== "undefined" &&
      (window.location.hostname.includes("localhost") || window.location.hostname === "127.0.0.1");
    return isLocal ? `http://${subdomain}.localhost:8000${endpoint}` : endpoint;
  };

  const fetchCustomerOrders = useCallback(async () => {
    if (!customerUser?.email && !customerUser?.phone) return;
    setLoadingOrders(true);
    try {
      const token = localStorage.getItem(`branch_customer_token_${branch.id}`) || localStorage.getItem("customer_token") || "";
      const queryParams = new URLSearchParams({
        branch_id: String(branch.id),
        email: customerUser.email || "",
      });
      if (customerUser.phone) queryParams.append("phone", customerUser.phone);

      const res = await fetch(getApiUrl(`/api/v1/orders/?${queryParams.toString()}`), {
        headers: {
          Accept: "application/json",
          "X-Branch-ID": String(branch.id),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (res.ok) {
        const data = await res.json();
        setOrders(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error("Error fetching branch customer orders:", err);
    } finally {
      setLoadingOrders(false);
    }
  }, [branch.id, customerUser?.email, customerUser?.phone, subdomain]);

  useEffect(() => {
    fetchCustomerOrders();
  }, [fetchCustomerOrders]);

  // 🌟 যদি চেকআউট মোডে থাকে তবে ফুল চেকআউট পেজ দেখাবে
  if (activeTab === "checkout") {
    return (
      <BranchCheckoutView
        branch={branch}
        customerUser={customerUser}
        subdomain={subdomain}
        primaryColor={primaryColor}
        onBack={() => setActiveTab("cart")}
        onOrderSuccess={() => {
          setActiveTab("orders");
          fetchCustomerOrders();
        }}
      />
    );
  }

  const filteredOrders = orders.filter((o) => {
    const matchSearch =
      o.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.items?.some((i: any) => i.product_name?.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchStatus = statusFilter === "all" || o.status?.toLowerCase() === statusFilter.toLowerCase();
    return matchSearch && matchStatus;
  });

  const totalSpent = orders.reduce((sum, o) => (o.status !== "cancelled" ? sum + (Number(o.total) || 0) : sum), 0);
  const pendingCount = orders.filter((o) => o.status === "pending" || o.status === "processing").length;
  const completedCount = orders.filter((o) => o.status === "delivered").length;
  const cancelledCount = orders.filter((o) => o.status === "cancelled").length;

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return { label: "Pending", style: "bg-amber-50 text-amber-700 border-amber-200" };
      case "processing":
        return { label: "Processing", style: "bg-blue-50 text-blue-700 border-blue-200" };
      case "shipped":
        return { label: "Shipped", style: "bg-purple-50 text-purple-700 border-purple-200" };
      case "delivered":
        return { label: "Delivered", style: "bg-emerald-50 text-emerald-700 border-emerald-200" };
      case "cancelled":
        return { label: "Cancelled", style: "bg-rose-50 text-rose-700 border-rose-200" };
      default:
        return { label: status, style: "bg-slate-100 text-slate-700 border-slate-200" };
    }
  };

  const initial = (customerUser?.first_name || customerUser?.email || "C").charAt(0).toUpperCase();

  return (
    <div
      style={{ ["--branch-primary" as any]: primaryColor }}
      className="min-h-screen bg-[#f8fafc] text-slate-800 font-sans"
    >
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToStore}
              className="p-2 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex items-center gap-1.5 text-xs font-bold cursor-pointer active:scale-95"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Store</span>
            </button>
            <div className="flex items-center gap-2">
              <div
                style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                className="p-1.5 rounded-xl hidden sm:block"
              >
                <Store className="w-4 h-4" />
              </div>
              <div>
                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600 block leading-none">
                  Outlet Customer Portal
                </span>
                <h1 className="text-sm sm:text-base font-black text-slate-900 truncate">
                  {branch.name}
                </h1>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchCustomerOrders}
              className="p-2 rounded-xl border border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50 cursor-pointer"
              title="Refresh Orders"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingOrders ? "animate-spin text-emerald-600" : ""}`} />
            </button>
            <button
              onClick={onLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-rose-200 bg-rose-50/50 hover:bg-rose-100/70 text-rose-600 text-xs font-bold transition-all cursor-pointer active:scale-95"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <aside className="lg:col-span-4 space-y-5">
            <div className="bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-6 shadow-sm space-y-5">
              <div className="flex items-center gap-3.5">
                <div
                  style={{ backgroundColor: primaryColor }}
                  className="w-14 h-14 rounded-2xl text-white font-black text-xl flex items-center justify-center shadow-lg shadow-emerald-600/20 shrink-0"
                >
                  {initial}
                </div>
                <div className="min-w-0">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <ShieldCheck className="w-3 h-3" />
                    Verified Outlet Member
                  </span>
                  <h2 className="text-base font-black text-slate-900 truncate mt-1">
                    {customerUser?.first_name || customerUser?.name || "Customer"}
                  </h2>
                  <p className="text-xs text-slate-400 font-mono truncate">{customerUser?.email}</p>
                </div>
              </div>

              <nav className="space-y-1.5 pt-2 border-t border-slate-100">
                <button
                  onClick={() => setActiveTab("orders")}
                  className={`w-full flex items-center justify-between p-3 rounded-2xl text-xs font-bold transition-all cursor-pointer ${
                    activeTab === "orders"
                      ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/25"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <ShoppingBag className="w-4 h-4" />
                    <span>My Branch Orders</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold ${activeTab === "orders" ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>
                    {orders.length}
                  </span>
                </button>

                <button
                  onClick={() => setActiveTab("cart")}
                  className={`w-full flex items-center justify-between p-3 rounded-2xl text-xs font-bold transition-all cursor-pointer ${
                    activeTab === "cart"
                      ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/25"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Package className="w-4 h-4" />
                    <span>Branch Shopping Cart</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold ${activeTab === "cart" ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>
                    {totalItemCount}
                  </span>
                </button>

                <button
                  onClick={() => setActiveTab("profile")}
                  className={`w-full flex items-center justify-between p-3 rounded-2xl text-xs font-bold transition-all cursor-pointer ${
                    activeTab === "profile"
                      ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/25"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <User className="w-4 h-4" />
                    <span>Profile & Outlet Info</span>
                  </div>
                </button>
              </nav>
            </div>

            <div className="bg-emerald-900 text-white rounded-3xl p-5 space-y-3 shadow-lg shadow-emerald-950/20">
              <div className="flex items-center gap-2">
                <Store className="w-4 h-4 text-emerald-300" />
                <h4 className="text-xs font-black uppercase tracking-wider text-emerald-200">
                  {branch.name} Outlet Support
                </h4>
              </div>
              <p className="text-xs text-emerald-100 leading-relaxed">
                Need urgent assistance? Contact this outlet manager directly.
              </p>
              <div className="space-y-1.5 text-xs text-emerald-100 pt-1 border-t border-emerald-800">
                {branch.contact_phone && (
                  <div className="flex items-center gap-2 font-mono">
                    <Phone className="w-3.5 h-3.5 text-emerald-300" />
                    <span>{branch.contact_phone}</span>
                  </div>
                )}
                {branch.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{branch.address}</span>
                  </div>
                )}
              </div>
            </div>
          </aside>

          <section className="lg:col-span-8 space-y-5">
            {activeTab === "orders" && (
              <div className="space-y-5">
                <div className="bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-6 shadow-sm space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <div>
                      <h3 className="text-base font-black text-slate-900">Branch Order Management</h3>
                      <p className="text-xs text-slate-400">Track all your purchases in {branch.name}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] uppercase font-bold text-slate-400 block">Total Spent</span>
                      <span className="text-lg font-black font-mono text-emerald-600">
                        ৳{totalSpent.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase block">Total Orders</span>
                      <span className="text-xl font-black font-mono text-slate-900 mt-0.5 block">{orders.length}</span>
                    </div>
                    <div className="p-3.5 rounded-2xl bg-amber-50/70 border border-amber-100">
                      <span className="text-[10px] font-bold text-amber-700 uppercase block">Pending</span>
                      <span className="text-xl font-black font-mono text-amber-800 mt-0.5 block">{pendingCount}</span>
                    </div>
                    <div className="p-3.5 rounded-2xl bg-emerald-50/70 border border-emerald-100">
                      <span className="text-[10px] font-bold text-emerald-700 uppercase block">Completed</span>
                      <span className="text-xl font-black font-mono text-emerald-800 mt-0.5 block">{completedCount}</span>
                    </div>
                    <div className="p-3.5 rounded-2xl bg-rose-50/70 border border-rose-100">
                      <span className="text-[10px] font-bold text-rose-700 uppercase block">Canceled</span>
                      <span className="text-xl font-black font-mono text-rose-800 mt-0.5 block">{cancelledCount}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200/90 p-3 sm:p-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shadow-xs">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by Order ID or Product name..."
                      className="w-full pl-10 pr-4 py-2 rounded-xl text-xs font-medium bg-slate-50 border border-slate-200 outline-none focus:border-emerald-500 focus:bg-white"
                    />
                  </div>

                  <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none pb-1 sm:pb-0 text-xs font-bold">
                    {["all", "pending", "processing", "delivered", "cancelled"].map((st) => (
                      <button
                        key={st}
                        onClick={() => setStatusFilter(st)}
                        className={`px-3 py-1.5 rounded-xl capitalize transition-all cursor-pointer whitespace-nowrap border ${
                          statusFilter === st
                            ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                            : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                        }`}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                </div>

                {loadingOrders ? (
                  <div className="bg-white rounded-3xl border border-slate-200 py-16 text-center space-y-2">
                    <RefreshCw className="w-6 h-6 animate-spin text-emerald-600 mx-auto" />
                    <p className="text-xs font-bold text-slate-400">Loading branch orders...</p>
                  </div>
                ) : filteredOrders.length === 0 ? (
                  <div className="bg-white rounded-3xl border border-dashed border-slate-200 py-16 text-center space-y-3 p-4">
                    <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto" />
                    <h4 className="text-sm font-black text-slate-700">No Orders Found</h4>
                    <p className="text-xs text-slate-400 max-w-sm mx-auto">
                      You have not placed any orders in {branch.name} outlet yet.
                    </p>
                    <button
                      onClick={onBackToStore}
                      style={{ backgroundColor: primaryColor }}
                      className="px-5 py-2.5 rounded-2xl text-xs font-black text-white shadow-md hover:opacity-95 cursor-pointer"
                    >
                      Browse Outlet Catalog
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3.5">
                    {filteredOrders.map((ord) => {
                      const badge = getStatusBadge(ord.status);

                      return (
                        <div
                          key={ord.id}
                          className="bg-white rounded-3xl border border-slate-200/90 p-4 sm:p-5 hover:border-emerald-500/40 hover:shadow-md transition-all space-y-3.5"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                            <div className="flex items-center gap-3">
                              <span className="font-mono font-black text-sm text-slate-900">#{ord.order_number}</span>
                              <span className={`px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase border ${badge.style}`}>
                                {badge.label}
                              </span>
                            </div>

                            <div className="flex items-center gap-3 text-xs text-slate-400 font-mono">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {new Date(ord.created_at).toLocaleDateString("en-GB", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })}
                              </span>
                            </div>
                          </div>

                          <div className="space-y-2">
                            {ord.items?.map((it: any) => (
                              <div key={it.id} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                  <span className="font-bold text-slate-800">{it.product_name}</span>
                                  <span className="text-slate-400 font-mono">× {it.quantity}</span>
                                </div>
                                <span className="font-mono font-bold text-slate-900">
                                  ৳{Number(it.total_price).toLocaleString()}
                                </span>
                              </div>
                            ))}
                          </div>

                          <div className="flex items-center justify-between pt-3 border-t border-slate-100 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-400 font-bold">Total Bill:</span>
                              <span className="text-base font-black font-mono text-emerald-600">
                                ৳{Number(ord.total).toLocaleString()}
                              </span>
                              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                                {ord.payment_method || "COD"}
                              </span>
                            </div>

                            <button
                              onClick={() => setSelectedOrder(ord)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all cursor-pointer"
                            >
                              <span>Details</span>
                              <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 🌟 Shopping Cart Tab (Proceed to Quick Order চাপলে Checkout পেজে যাবে) */}
            {activeTab === "cart" && (
              <div className="bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-6 shadow-sm space-y-5">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="text-base font-black text-slate-900">My Branch Shopping Cart</h3>
                    <p className="text-xs text-slate-400">Review items saved in this branch outlet</p>
                  </div>
                  {cart.length > 0 && (
                    <button
                      onClick={clearCart}
                      className="text-xs font-bold text-rose-500 hover:underline flex items-center gap-1 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Clear Cart</span>
                    </button>
                  )}
                </div>

                {cart.length === 0 ? (
                  <div className="py-16 text-center space-y-3">
                    <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto" />
                    <h4 className="text-sm font-black text-slate-700">Your Branch Cart is Empty</h4>
                    <p className="text-xs text-slate-400 max-w-sm mx-auto">
                      Add items from {branch.name} outlet catalog to place an instant order.
                    </p>
                    <button
                      onClick={onBackToStore}
                      style={{ backgroundColor: primaryColor }}
                      className="px-5 py-2.5 rounded-2xl text-xs font-black text-white shadow-md hover:opacity-95 cursor-pointer"
                    >
                      Browse Outlet Products
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-3">
                      {cart.map((item) => (
                        <div
                          key={item.id}
                          className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50 flex items-center justify-between gap-4"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-14 h-14 rounded-xl bg-white border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
                              {item.image ? (
                                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <Package className="w-6 h-6 text-slate-300" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-xs font-black text-slate-900 truncate">{item.name}</h4>
                              <p className="text-xs font-mono font-bold text-emerald-600 mt-0.5">
                                ৳{item.price.toLocaleString()}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3 shrink-0">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateQuantity(item.id, -1)}
                                className="w-7 h-7 rounded-lg bg-white border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100 cursor-pointer"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="text-xs font-bold font-mono px-1">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.id, 1)}
                                className="w-7 h-7 rounded-lg bg-white border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100 cursor-pointer"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>

                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="p-1.5 text-slate-400 hover:text-rose-500 cursor-pointer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-100/80 space-y-3">
                      <div className="flex items-center justify-between text-xs font-bold text-slate-600">
                        <span>Total Items:</span>
                        <span>{totalItemCount} Items</span>
                      </div>
                      <div className="flex items-center justify-between text-sm font-black text-slate-900 border-t border-slate-200 pt-2">
                        <span>Total Bill:</span>
                        <span className="text-lg font-mono text-emerald-600">৳{cartTotal.toLocaleString()}</span>
                      </div>

                      {/* 🌟 সরাসরি পূর্ণাঙ্গ Checkout পেজ ওপেন হবে */}
                      <button
                        onClick={() => setActiveTab("checkout")}
                        style={{ backgroundColor: primaryColor }}
                        className="w-full py-3.5 rounded-2xl text-xs font-black text-white shadow-lg flex items-center justify-center gap-2 cursor-pointer hover:opacity-95 active:scale-95"
                      >
                        <span>Proceed to Checkout</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "profile" && (
              <div className="bg-white rounded-3xl border border-slate-200/90 p-5 sm:p-6 shadow-sm space-y-5">
                <div className="border-b border-slate-100 pb-3">
                  <h3 className="text-base font-black text-slate-900">Customer Profile & Outlet Association</h3>
                  <p className="text-xs text-slate-400">Details for {branch.name} outlet orders</p>
                </div>

                <div className="space-y-3 text-xs">
                  <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                    <span className="text-slate-500 font-bold flex items-center gap-2">
                      <User className="w-4 h-4 text-emerald-600" /> Full Name
                    </span>
                    <span className="font-black text-slate-900">
                      {customerUser?.first_name || customerUser?.name || "Customer"}
                    </span>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                    <span className="text-slate-500 font-bold flex items-center gap-2">
                      <Mail className="w-4 h-4 text-emerald-600" /> Email Address
                    </span>
                    <span className="font-mono font-bold text-slate-900">{customerUser?.email}</span>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                    <span className="text-slate-500 font-bold flex items-center gap-2">
                      <Phone className="w-4 h-4 text-emerald-600" /> Contact Phone
                    </span>
                    <span className="font-mono font-bold text-slate-900">
                      {customerUser?.phone || "Not set yet"}
                    </span>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                    <span className="text-slate-500 font-bold flex items-center gap-2">
                      <Store className="w-4 h-4 text-emerald-600" /> Registered Outlet
                    </span>
                    <span className="font-black text-emerald-700">{branch.name}</span>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    onClick={onLogout}
                    className="w-full py-3 rounded-2xl border border-rose-200 bg-rose-50 text-rose-600 text-xs font-black hover:bg-rose-100 transition-colors cursor-pointer active:scale-95"
                  >
                    Log Out of This Branch
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>

      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
          <div className="w-full max-w-lg rounded-3xl bg-white border border-slate-200 text-slate-900 shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-sm font-black text-slate-900">Order #{selectedOrder.order_number}</h3>
                <span className="text-[10px] text-slate-400 font-mono">
                  {new Date(selectedOrder.created_at).toLocaleDateString("en-GB")}
                </span>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-pointer"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1 text-xs">
              <span className="text-[10px] uppercase font-bold text-slate-400 block">Delivery Address</span>
              <p className="font-bold text-slate-900">{selectedOrder.customer_name || customerUser?.first_name}</p>
              <p className="font-mono text-slate-600">{selectedOrder.customer_phone || customerUser?.phone}</p>
              <p className="text-slate-500 leading-relaxed">
                {selectedOrder.full_address || selectedOrder.shipping_address?.address || "Address on file"}
              </p>
            </div>

            <div className="space-y-2">
              <span className="text-[10px] uppercase font-bold text-slate-400 block">Ordered Items</span>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {selectedOrder.items?.map((it: any) => (
                  <div key={it.id} className="p-2.5 rounded-xl border border-slate-100 flex items-center justify-between text-xs">
                    <div>
                      <p className="font-bold text-slate-800">{it.product_name}</p>
                      <span className="text-[10px] text-slate-400 font-mono">Quantity: {it.quantity}</span>
                    </div>
                    <span className="font-mono font-bold text-emerald-600">
                      ৳{Number(it.total_price).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-emerald-50/70 border border-emerald-100 flex items-center justify-between text-xs font-bold">
              <span className="text-emerald-900">Total Order Amount:</span>
              <span className="text-base font-black font-mono text-emerald-700">
                ৳{Number(selectedOrder.total).toLocaleString()}
              </span>
            </div>

            <button
              onClick={() => setSelectedOrder(null)}
              className="w-full py-2.5 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}